/**
 * Integer arithmetic for ticks, days and years (1.K-1 rule 9): JavaScript numbers within
 * ±(2^53 − 1), floor division for negative values, and a safe-integer check before every
 * multiplication. An operation whose exact result is not a safe integer gives `NaN`, which every
 * later operation carries on, so a caller checks once, at the end, with `Number.isSafeInteger`
 * (or `isTick`). Never `BigInt`.
 */
export const MAX_SAFE = Number.MAX_SAFE_INTEGER;
/** The exact sum, or NaN when it is not a safe integer. */
export function add(a, b) {
    const r = a + b;
    return Number.isSafeInteger(r) ? r + 0 : Number.NaN;
}
/** The exact difference, or NaN when it is not a safe integer. */
export function sub(a, b) {
    const r = a - b;
    return Number.isSafeInteger(r) ? r + 0 : Number.NaN;
}
/** The exact product, checked before multiplying; NaN when it would not be a safe integer. */
export function mul(a, b) {
    const limit = b === 0 ? Number.POSITIVE_INFINITY : MAX_SAFE / Math.abs(b);
    if (!(Math.abs(a) <= limit))
        return Number.NaN;
    const r = a * b;
    return Number.isSafeInteger(r) ? r + 0 : Number.NaN;
}
/** Floor modulo for a positive integer `m`: the result lies in [0, m). */
export function floorMod(a, m) {
    const r = a % m;
    return r < 0 ? r + m : r + 0;
}
/** Floor division for a positive integer `m`, exact for every safe integer `a`. */
export function floorDiv(a, m) {
    const r = a % m;
    // a − r is a multiple of m no larger than |a|, so it and the quotient are exact.
    const q = (a - r) / m;
    return r < 0 ? q - 1 : q + 0;
}
/**
 * Floor modulo for real numbers (moon phases), with a positive finite modulus: in [0, m). Above
 * half the largest double, `r + m` overflows; `r` is then not negative, so it is the answer.
 */
export function floorModReal(a, m) {
    const r = a % m;
    const s = r + m;
    return Number.isFinite(s) ? (s % m) + 0 : r + 0;
}
//# sourceMappingURL=math.js.map